import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Receipt, Plus, X, Trash2, CheckCircle2, AlertTriangle, XCircle, Clock } from 'lucide-react';
import {
  AdminStoreItemResource,
  AvailableSubscriptionResource,
  BundleItemType,
  ManualPurchaseExtraItem,
  ManualPurchasePreview,
  ManualPurchaseResource,
  ManualPurchaseStatus,
  SubscriptionBundleResource,
} from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import ManualPurchaseDetailSlideOver, { manualPurchaseStatusBadge } from '../components/ManualPurchaseDetailSlideOver';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../utils/helpers';

const MONTH_OPTIONS = [1, 3, 6, 12, 24];
const MAX_PROOF_BYTES = 5 * 1024 * 1024;

interface ExtraRow {
  itemType: BundleItemType;
  id: string;
  months: number | null;
}

const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const errorMessage = (err: any, fallback: string): string => {
  const errors = err?.response?.data?.errors;
  if (errors) {
    const first = Object.values(errors)[0];
    if (Array.isArray(first) && first.length) return String(first[0]);
  }
  return err?.response?.data?.message || fallback;
};

const contentSummary = (p: ManualPurchaseResource) => {
  const extras = p.items.filter((i) => !i.isFromBundle).length;
  if (p.bundle?.name) return `${p.bundle.name}${extras ? ` + ${extras} item` : ''}`;
  return p.items.map((i) => i.name).join(', ');
};

const ManualPurchases: React.FC = () => {
  const { canManage } = useAuth();
  const [purchases, setPurchases] = useState<ManualPurchaseResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ManualPurchaseStatus | ''>('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const perPage = 15;

  const [bundles, setBundles] = useState<SubscriptionBundleResource[]>([]);
  const [subscriptions, setSubscriptions] = useState<AvailableSubscriptionResource[]>([]);
  const [storeItems, setStoreItems] = useState<AdminStoreItemResource[]>([]);

  const [formOpen, setFormOpen] = useState(false);
  const [bundleId, setBundleId] = useState('');
  const [extras, setExtras] = useState<ExtraRow[]>([]);
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey());
  const [preview, setPreview] = useState<ManualPurchasePreview | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getManualPurchases(page, perPage, { search: search || undefined, status: status || undefined });
      setPurchases(res.data);
      setTotal(res.meta.total);
    } catch (e) {
      console.error('Error loading manual purchases:', e);
      setPurchases([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  const openForm = () => {
    setBundleId('');
    setExtras([]);
    setEmail('');
    setNotes('');
    setProof(null);
    setPreview(null);
    setPreviewError('');
    setFormError('');
    setIdempotencyKey(newIdempotencyKey());
    setFormOpen(true);
    api.getBundles(1, 100, { availableOnly: true }).then((res) => setBundles(res.data)).catch(() => setBundles([]));
    api.getSubscriptions(1, 100).then((res) => setSubscriptions(res.data)).catch(() => setSubscriptions([]));
    api.getAdminStoreItems(1, 100, undefined, { isActive: true }).then((res) => setStoreItems(res.data)).catch(() => setStoreItems([]));
  };

  const extraPayload: ManualPurchaseExtraItem[] = useMemo(() => extras
    .filter((row) => row.id)
    .map((row) => (row.itemType === 'subscription'
      ? { itemType: 'subscription', subscriptionId: row.id, months: row.months }
      : { itemType: 'store_item', storeItemId: row.id, months: row.months })), [extras]);

  // Ringkasan + cek email dihitung backend (aturan yang sama dengan saat submit).
  useEffect(() => {
    if (!formOpen) return;
    if (!bundleId && extraPayload.length === 0) { setPreview(null); setPreviewError(''); return; }
    const timer = setTimeout(() => {
      api.previewManualPurchase({ bundleId: bundleId || null, extraItems: extraPayload, email: email.trim() || undefined })
        .then((res) => { setPreview(res); setPreviewError(''); })
        .catch((err) => { setPreview(null); setPreviewError(errorMessage(err, 'Ringkasan tidak bisa dihitung.')); });
    }, 400);
    return () => clearTimeout(timer);
  }, [formOpen, bundleId, extraPayload, email]);

  const selectedBundle = bundles.find((b) => b.id === bundleId);
  const bundleStoreItemIds = selectedBundle?.items.map((i) => i.storeItem?.id).filter(Boolean) ?? [];
  const hasSubscription = Boolean(selectedBundle) || extras.some((row) => row.itemType === 'subscription');
  const emailCheck = preview?.emailCheck ?? null;
  const blocked = emailCheck?.state === 'blocked';
  const canSubmit = Boolean(preview && email.trim() && proof && !blocked && !previewError && !submitting);

  const updateExtra = (index: number, patch: Partial<ExtraRow>) =>
    setExtras((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const onExtraItemChange = (index: number, id: string) => {
    const row = extras[index];
    if (row.itemType === 'store_item') {
      const item = storeItems.find((s) => s.id === id);
      updateExtra(index, { id, months: item?.durationType === 'lifetime' ? null : 12 });
    } else {
      updateExtra(index, { id, months: row.months ?? 12 });
    }
  };

  const onProofChange = (file: File | null) => {
    setFormError('');
    if (file && file.size > MAX_PROOF_BYTES) { setFormError('Ukuran bukti bayar maksimal 5 MB.'); setProof(null); return; }
    setProof(file);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !proof || !preview) return;
    const warnings = emailCheck?.warnings ?? [];
    const target = emailCheck?.state === 'has_company'
      ? `langsung AKTIF di ${emailCheck.company?.brandName}`
      : 'MENUNGGU DAFTAR (aktif otomatis saat email ini mendaftar)';
    const confirmText = [
      `Simpan pembelian ${formatCurrency(preview.grandTotal)} untuk ${email.trim()}?`,
      `Paket akan ${target}, dan email pembelian dikirim.`,
      ...warnings.map((w) => `• ${w}`),
    ].join('\n');
    if (!window.confirm(confirmText)) return;

    try {
      setSubmitting(true);
      setFormError('');
      const res = await api.createManualPurchase({
        bundleId: bundleId || null,
        extraItems: extraPayload,
        email: email.trim(),
        notes: notes.trim() || undefined,
        idempotencyKey,
        paymentProof: proof,
      });
      setFormOpen(false);
      setNotice(res.message);
      setPage(1);
      await load();
    } catch (err: any) {
      setFormError(errorMessage(err, 'Gagal menyimpan pembelian.'));
    } finally {
      setSubmitting(false);
    }
  };

  const emailStatus = () => {
    if (!email.trim()) return null;
    if (!emailCheck) return null;
    const map = {
      has_company: { icon: CheckCircle2, cls: 'text-green-700', text: `Terdaftar: aktif langsung di ${emailCheck.company?.brandName ?? 'company ini'}` },
      registered_no_company: { icon: Clock, cls: 'text-yellow-700', text: 'Terdaftar, belum punya usaha: aktif otomatis saat melengkapi pendaftaran' },
      unregistered: { icon: Clock, cls: 'text-yellow-700', text: 'Belum terdaftar: aktif otomatis saat mendaftar dengan email ini' },
      blocked: { icon: XCircle, cls: 'text-red-700', text: 'Tidak bisa diproses' },
    }[emailCheck.state];
    const Icon = map.icon;
    return (
      <div className={`mt-1 text-sm ${map.cls}`}>
        <div className="flex items-center gap-1"><Icon className="h-4 w-4" /> {map.text}</div>
        {emailCheck.blockedReasons.map((r) => <div key={r} className="ml-5">{r}</div>)}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Receipt className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Pembelian Manual</h1>
        </div>
        {canManage && (
          <button onClick={openForm} className="inline-flex items-center gap-1 bg-primary-600 text-white rounded-md px-4 py-2 hover:bg-primary-700">
            <Plus className="h-4 w-4" /> Pembelian baru
          </button>
        )}
      </div>

      {notice && (
        <div className="mb-4 p-3 rounded-md bg-green-50 text-green-700 text-sm flex justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice('')}><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Cari email..."
          className="w-full sm:w-80 border border-gray-300 rounded-md px-3 py-2" />
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value as ManualPurchaseStatus | ''); }}
          className="w-full sm:w-52 border border-gray-300 rounded-md px-3 py-2">
          <option value="">Semua status</option>
          <option value="active">Aktif</option>
          <option value="pending_registration">Menunggu Daftar</option>
          <option value="cancelled">Dibatalkan</option>
        </select>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2">Tanggal</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">Isi</th>
                <th className="px-4 py-2">Total</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Admin</th>
                <th className="px-4 py-2">Bukti</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {purchases.map((p) => (
                <tr key={p.id} onClick={() => setDetailId(p.id)} className="cursor-pointer hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap">{formatDate(p.createdAt)}</td>
                  <td className="px-4 py-2">
                    {p.email}
                    {p.company && <div className="text-xs text-gray-500">{p.company.brandName}</div>}
                  </td>
                  <td className="px-4 py-2">{contentSummary(p)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{formatCurrency(p.grandTotal)}</td>
                  <td className="px-4 py-2">
                    {manualPurchaseStatusBadge(p.status)}
                    {p.lastActivationError && p.status === 'pending_registration' && (
                      <AlertTriangle className="inline h-4 w-4 ml-1 text-yellow-600" aria-label="Aktivasi otomatis gagal" />
                    )}
                  </td>
                  <td className="px-4 py-2">{p.createdBy?.fullName ?? '-'}</td>
                  <td className="px-4 py-2 text-primary-600">Lihat</td>
                </tr>
              ))}
              {purchases.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Belum ada pembelian manual.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <Pagination currentPage={page} total={total} perPage={perPage} onPageChange={setPage} />
      </div>

      <ManualPurchaseDetailSlideOver purchaseId={detailId} onClose={() => setDetailId(null)} onChanged={load} />

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-500 bg-opacity-75 p-4">
          <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Pembelian manual baru</h2>
              <button type="button" onClick={() => setFormOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 px-6 py-5">
              <div className="lg:col-span-2 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bundling</label>
                  <select value={bundleId} onChange={(e) => {
                    setBundleId(e.target.value);
                    if (e.target.value) setExtras((rows) => rows.filter((r) => r.itemType !== 'subscription'));
                  }} className="w-full border border-gray-300 rounded-md px-3 py-2">
                    <option value="">Tanpa bundling</option>
                    {bundles.map((b) => <option key={b.id} value={b.id}>{b.name} — {formatCurrency(b.pricing.total)}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{bundleId ? 'Item tambahan (opsional)' : 'Item'}</label>
                  <div className="space-y-2">
                    {extras.map((row, index) => {
                      const storeItem = row.itemType === 'store_item' ? storeItems.find((s) => s.id === row.id) : undefined;
                      const usedIds = extras.filter((_, i) => i !== index).map((r) => r.id);
                      return (
                        <div key={index} className="flex gap-2">
                          <select value={row.id} onChange={(e) => onExtraItemChange(index, e.target.value)}
                            className="flex-1 border border-gray-300 rounded-md px-3 py-2">
                            <option value="">{row.itemType === 'subscription' ? 'Pilih paket' : 'Pilih store item'}</option>
                            {row.itemType === 'subscription'
                              ? subscriptions.map((s) => <option key={s.id} value={s.id}>Langganan {s.name}</option>)
                              : storeItems
                                .filter((s) => !bundleStoreItemIds.includes(s.id) && !usedIds.includes(s.id))
                                .map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                          {storeItem?.durationType === 'lifetime' ? (
                            <div className="w-32 border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-gray-600">Lifetime</div>
                          ) : (
                            <select value={row.months ?? 12} onChange={(e) => updateExtra(index, { months: Number(e.target.value) })}
                              className="w-32 border border-gray-300 rounded-md px-3 py-2">
                              {MONTH_OPTIONS.map((m) => <option key={m} value={m}>{m} bulan</option>)}
                            </select>
                          )}
                          <button type="button" onClick={() => setExtras((rows) => rows.filter((_, i) => i !== index))}
                            className="text-gray-400 hover:text-red-600 px-2"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex gap-4 text-sm">
                    <button type="button" onClick={() => setExtras((rows) => [...rows, { itemType: 'store_item', id: '', months: 12 }])}
                      className="font-medium text-primary-600 hover:underline">+ Tambah store item</button>
                    {!hasSubscription && (
                      <button type="button" onClick={() => setExtras((rows) => [...rows, { itemType: 'subscription', id: '', months: 12 }])}
                        className="font-medium text-primary-600 hover:underline">+ Tambah langganan</button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email customer</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="pemilik@usaha.id" />
                  {emailStatus()}
                  {(emailCheck?.warnings.length ?? 0) > 0 && (
                    <div className="mt-2 p-3 rounded-md bg-yellow-50 border border-yellow-200 text-sm text-yellow-900 space-y-1">
                      {emailCheck!.warnings.map((w) => <div key={w}>{w}</div>)}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bukti bayar (JPG/PNG/WebP/PDF, maks 5 MB)</label>
                  <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={(e) => onProofChange(e.target.files?.[0] ?? null)}
                    className="w-full text-sm border border-dashed border-gray-300 rounded-md px-3 py-3" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catatan (opsional)</label>
                  <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Booth B12, transfer BCA" />
                </div>
              </div>

              <div>
                <div className="bg-gray-50 border rounded-lg p-4 text-sm">
                  <h3 className="font-semibold text-gray-900 mb-3">Ringkasan</h3>
                  {preview ? (
                    <div className="space-y-1.5">
                      {preview.items.map((item, i) => (
                        <div key={i} className="flex justify-between gap-2">
                          <span>{item.name} {item.months ? `${item.months} bln` : 'lifetime'}{!item.isFromBundle && bundleId ? ' *' : ''}</span>
                          <span className="whitespace-nowrap">{formatCurrency(item.normalPrice)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between border-t pt-2 mt-2"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(preview.subtotal)}</span></div>
                      {preview.bundleDiscount > 0 && (
                        <div className="flex justify-between"><span className="text-gray-500">Diskon bundling</span><span className="text-red-600">−{formatCurrency(preview.bundleDiscount)}</span></div>
                      )}
                      <div className="flex justify-between border-t pt-2 font-semibold text-base"><span>Total bayar</span><span>{formatCurrency(preview.grandTotal)}</span></div>
                      {bundleId && extras.length > 0 && <p className="text-xs text-gray-500">* item tambahan, tidak kena diskon bundling</p>}
                    </div>
                  ) : (
                    <p className="text-gray-500">{previewError || 'Pilih bundling atau tambahkan item.'}</p>
                  )}
                </div>
                <button type="submit" disabled={!canSubmit}
                  className="mt-4 w-full bg-primary-600 text-white rounded-md px-4 py-2 font-medium hover:bg-primary-700 disabled:opacity-50">
                  {submitting ? 'Menyimpan...' : 'Simpan & aktifkan'}
                </button>
                <p className="mt-2 text-xs text-gray-500">Aktif setelah email, bukti bayar, dan isi pembelian valid. Ada konfirmasi sebelum disimpan.</p>
              </div>
            </div>

            {formError && <div className="mx-6 mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{formError}</div>}
          </form>
        </div>
      )}
    </div>
  );
};

export default ManualPurchases;
